import Track from '../../../models/track';
import { compareSortValues } from '../../playlist/playlistSorters';
import { TrackProperty } from '../../../services/columnSettings/playlistColumns';

/**
 * Sorting for the streaming search results.
 *
 * The playlists sort themselves with playlistSorters.ts, which reads the
 * property straight off a Track. Search results cannot: a row there is a
 * SoundCloud track, a Mixcloud show or a YouTube video as the API sent it,
 * and nothing in it is called `releaseDate`. So each page hands the list a
 * function that answers "what is this item's value for this column", and the
 * comparison is playlistSorters' own compareSortValues.
 */
export type SortValue<T> = (item: T, prop: TrackProperty) => string | number | undefined;

/**
 * A SortValue for a list whose items already know how to become Tracks -
 * which is every track-shaped search result, because adding one to the
 * library needs the same mapper.
 *
 * The mapping is cached per item in a WeakMap rather than run inside the
 * comparator: a sort of 200 results calls the comparator well over a
 * thousand times, and building a Track for each side of each comparison
 * would be the whole cost of the sort. The cache lives as long as the
 * results do and dies with them.
 *
 * Build this once per page - at module scope, or in a useMemo - so the cache
 * survives a re-render.
 */
export function createTrackSortValue<T extends object>(mapper: (item: T) => Track): SortValue<T> {
  const cache = new WeakMap<T, Track>();

  return function trackSortValue(item, prop) {
    let track = cache.get(item);

    if (!track) {
      track = mapper(item);
      cache.set(item, track);
    }

    return track[prop];
  };
}

/**
 * Which way a column is sorted, if it is the one being sorted by. Drives the
 * header's arrow and the class the playlist header uses for the same thing.
 */
export type SortDirection = 'ascending' | 'descending';

export interface SearchResultSort {
  prop: TrackProperty;
  direction: SortDirection;
}

/**
 * The results in sort order. Returns the original array untouched when
 * nothing is sorted, so an unsorted list costs nothing and keeps the
 * identity its consumers memoise on.
 */
export function sortResults<T>(
  results: T[],
  sort: SearchResultSort | undefined,
  sortValue: SortValue<T> | undefined,
) {
  if (!sort || !sortValue) {
    return results;
  }

  const direction = sort.direction === 'descending' ? -1 : 1;

  // A copy: the array belongs to the page's state, and sorting it in place
  // would reorder what the next page of results is appended to.
  return results
    .slice()
    .sort(
      (a, b) => direction * compareSortValues(sortValue(a, sort.prop), sortValue(b, sort.prop)),
    );
}
